/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

// TODO: Remove the `ts-nocheck` when ready.

import { gsap } from 'gsap';
import {
    BufferGeometry,
    Color,
    Float32BufferAttribute,
    Mesh,
    Object3D,
    PerspectiveCamera,
    Scene,
    ShaderMaterial,
    type Texture,
    Vector2,
    WebGLRenderTarget,
} from 'three';

import type Renderer from '../../globals/Renderer';

import { waitForNextFrame } from '../../utils';
import Loaders from '../../utils/Loaders';
import PerformanceProfile, { GPU_RANGE } from '../../utils/PerformanceProfile';

import { fragmentShader, vertexShader } from './shaders';

export default class BlurBackground extends Object3D {
    camera: PerspectiveCamera;
    renderer: Renderer;
    scene: Scene;
    // TODO: It appears that we want to pass a generic to `Mesh`
    // that extends `Material` with a `uniform` property.
    mesh: Mesh;
    rtHorizontal: WebGLRenderTarget;
    rtVertical: WebGLRenderTarget;

    // layer: Layer;
    texture?: Texture;

    width = 1;
    height = 1;
    // Iteration needs to be a even number
    blurIterations = 8;
    textureLoaded = false;

    constructor(renderer: Renderer) {
        super();

        this.renderer = renderer;

        this.scene = new Scene();
        this.scene.add(this);

        this.camera = new PerspectiveCamera();

        this.rtHorizontal = new WebGLRenderTarget();
        this.rtVertical = new WebGLRenderTarget();

        // TODO: Does `setSize` just update the `width` and `height`
        // values for the `WebGLRenderTarget`? If so, then we should
        // simply pass these values to the constructor.
        this.rtHorizontal.setSize(1024, 1024);
        this.rtVertical.setSize(1024, 1024);

        const geometry = new BufferGeometry();

        geometry.setAttribute('position', new Float32BufferAttribute([-1, 3, 0, -1, -1, 0, 3, -1, 0], 3));
        geometry.setAttribute('uv', new Float32BufferAttribute([0, 2, 0, 0, 2, 0], 2));

        const material = new ShaderMaterial({
            uniforms: {
                uTexture: { value: null },
                uResolution: { value: new Vector2(this.width, this.height) },
                uTextureAspectRatio: { value: new Vector2(1, 1) },
                uRenderMode: { value: 0 },
                uOverlayColor: { value: new Color('#090719') },
                uOverlayAlpha: { value: 0.5 },
                uRadius: { value: 0 },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
        });

        this.mesh = new Mesh(geometry, material);
        this.add(this.mesh);
    }

    // TODO: Type these arguments.
    updateImage(imgUrl, options) {
        if (options.iterations) {
            this.blurIterations = options.iterations;
        }

        if (options.overlayColor) {
            this.mesh.material.uniforms.uOverlayColor.value.setStyle(options.overlayColor);
        }

        if (options.overlayAlpha) {
            this.mesh.material.uniforms.uOverlayAlpha.value = options.overlayAlpha;
        }

        if (options.domElement) {
            this.domParent = options.domElement;
        }

        this.layer.canRender = false;

        Loaders.textureLoader.load(imgUrl, (texture) => {
            this.texture = texture;
            this.texture.needsUpdate = true;
            this.texture.flipY = false;

            // AspectRatio
            const textureWidth = texture.image.width || 0;
            const textureHeight = texture.image.height || 0;

            let aspectRatioX = 1.0;
            let aspectRatioY = 1.0;

            if (textureWidth > 0) {
                aspectRatioX = textureWidth < textureHeight ? 1.0 : textureHeight / textureWidth;
                aspectRatioY = textureWidth < textureHeight ? textureWidth / textureHeight : 1.0;
            }

            this.mesh.material.uniforms.uTextureAspectRatio.value.x = aspectRatioX;
            this.mesh.material.uniforms.uTextureAspectRatio.value.y = aspectRatioY;

            this.textureLoaded = true;
            this.renderFrame();
        });
    }

    removeImage() {
        this.textureLoaded = false;

        if (this.texture) this.texture.dispose();

        this.mesh.material.uniforms.uTexture.value = null;
        this.mesh.visible = false;

        // Clean blur layer: render empty frame.
        if (!this.domParent) this.layer.renderFrame();

        if (this.currentImg) {
            this.currentImg.remove();
            delete this.currentImg;
        }
    }

    setSize(width: number, height: number) {
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        this.mesh.material.uniforms.uResolution.value.x = width;
        this.mesh.material.uniforms.uResolution.value.y = height;

        if (this.visible) this.renderFrame(true);
    }

    async renderFrame(aspectRatioOnly = false) {
        if (!this.textureLoaded) return;

        this.mesh.visible = true;

        if (!aspectRatioOnly) {
            for (let i = 0; i < this.blurIterations; i++) {
                const radius = this.blurIterations - i - 1;

                if (i % 2.0 == 0.0) {
                    // Render Horizontal
                    // Render Texture on first frame
                    this.mesh.material.uniforms.uTexture.value = i === 0 ? this.texture : this.rtVertical.texture;

                    this.mesh.material.uniforms.uRadius.value = radius;
                    this.mesh.material.uniforms.uRenderMode.value = 0.0;

                    this.renderer.setRenderTarget(this.rtHorizontal);
                    this.renderer.render(this.scene, this.camera);
                    this.renderer.setRenderTarget(null);
                } else {
                    // Render Vertical
                    this.mesh.material.uniforms.uRadius.value = radius;
                    this.mesh.material.uniforms.uRenderMode.value = 1.0;
                    this.mesh.material.uniforms.uTexture.value = this.rtHorizontal.texture;

                    this.renderer.setRenderTarget(this.rtVertical);
                    this.renderer.render(this.scene, this.camera);
                    this.renderer.setRenderTarget(null);
                }

                if (PerformanceProfile.range === GPU_RANGE.HIGH) {
                    // Render twice per frame on High GPU Range
                    if (i % 2.0 == 0.0) await waitForNextFrame();
                } else {
                    // Render once per frame
                    await waitForNextFrame();
                }
            }

            this.mesh.material.uniforms.uTexture.value = this.rtVertical.texture;
        }

        // Render Normal
        this.mesh.material.uniforms.uRenderMode.value = 2.0;

        if (this.domParent) {
            this.generateImage(!aspectRatioOnly);
        } else {
            this.layer.renderFrame();
        }

        if (!aspectRatioOnly) this.layer.animateIn('fade-in');
    }

    async generateImage(animateIn = true) {
        this.renderer.setRenderTarget(this.rtHorizontal);
        this.renderer.render(this.scene, this.camera);
        this.renderer.setRenderTarget(null);
        await waitForNextFrame();

        const pixelData = new Uint8Array(1024 * 1024 * 4);

        // Expensive function - Wait next frame to avoid blocking
        this.renderer.readRenderTargetPixels(this.rtHorizontal, 0, 0, 1024, 1024, pixelData);
        await waitForNextFrame();

        let canvas = document.createElement('canvas');
        canvas.width = 1024;
        canvas.height = 1024;

        let context = canvas.getContext('2d');
        let imageData = context.createImageData(1024, 1024);
        imageData.data.set(pixelData);

        // Expensive function - Wait next frame to avoid blocking
        context.putImageData(imageData, 0, 0);
        await waitForNextFrame();

        const img = document.createElement('img');
        img.src = canvas.toDataURL();

        if (this.currentImg) {
            this.currentImg.remove();
            delete this.currentImg;
        }

        this.currentImg = img;

        this.domParent.appendChild(img);

        if (animateIn) {
            gsap.fromTo(img, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power1.out' });
        }

        canvas = null;
        context = null;
        imageData = null;
    }

    delete() {
        // TODO: Implement method.
    }
}
